import { LOGIN_PATH, OIDC_CALLBACK_PATH } from "@airange/shared/oidc-routes";
import { resolveMicroLabAdminPageTitleId } from "./page-title-rules";

export type MicroLabBreadcrumbItem = {
  path: string;
  breadcrumbName: string;
};

type BreadcrumbLabelResolver = (descriptor: { id: string }) => string;

type BreadcrumbTemplate = {
  match: RegExp;
  items: Array<{
    path: string;
    labelId: string;
  }>;
};

const MICRO_LAB_BREADCRUMB_TEMPLATES: BreadcrumbTemplate[] = [
  {
    match: /^\/members$/,
    items: [{ path: "/members", labelId: "microLab.pages.members.title" }],
  },
  {
    match: /^\/schemes$/,
    items: [{ path: "/schemes", labelId: "microLab.pages.schemes.title" }],
  },
  {
    match: /^\/schemes\/reviews$/,
    items: [
      { path: "/schemes", labelId: "microLab.pages.schemes.title" },
      { path: "/schemes/reviews", labelId: "microLab.pages.schemeReviews.title" },
    ],
  },
  {
    match: /^\/schemes\/[^/]+\/versions$/,
    items: [
      { path: "/schemes", labelId: "microLab.pages.schemes.title" },
      { path: "/schemes/:schemeCode/versions", labelId: "microLab.pages.schemeVersions.title" },
    ],
  },
  {
    match: /^\/releases$/,
    items: [{ path: "/releases", labelId: "microLab.pages.releases.title" }],
  },
  {
    match: /^\/releases\/create$/,
    items: [
      { path: "/releases", labelId: "microLab.pages.releases.title" },
      { path: "/releases/create", labelId: "microLab.pages.releaseCreate.title" },
    ],
  },
  {
    match: /^\/releases\/[^/]+$/,
    items: [
      { path: "/releases", labelId: "microLab.pages.releases.title" },
      { path: "/releases/:releaseCode", labelId: "microLab.pages.releaseDetail.title" },
    ],
  },
  {
    match: /^\/runs$/,
    items: [{ path: "/runs", labelId: "microLab.pages.runs.title" }],
  },
];

function isBreadcrumbDisabledPath(pathname: string) {
  return pathname === LOGIN_PATH || pathname === OIDC_CALLBACK_PATH;
}

function resolveBreadcrumbTemplate(pathname: string) {
  return MICRO_LAB_BREADCRUMB_TEMPLATES.find((template) => template.match.test(pathname)) ?? null;
}

export function buildMicroLabAdminBreadcrumbs(
  pathname: string,
  resolveLabel: BreadcrumbLabelResolver,
): MicroLabBreadcrumbItem[] {
  if (isBreadcrumbDisabledPath(pathname)) {
    return [];
  }

  const rootItem: MicroLabBreadcrumbItem = {
    path: "/",
    breadcrumbName: resolveLabel({ id: "microLab.shell.title" }),
  };

  const template = resolveBreadcrumbTemplate(pathname);
  if (template) {
    return [
      rootItem,
      ...template.items.map((item) => ({
        path: item.path,
        breadcrumbName: resolveLabel({ id: item.labelId }),
      })),
    ];
  }

  const fallbackLabelId = resolveMicroLabAdminPageTitleId(pathname);
  if (!fallbackLabelId) {
    return [];
  }

  return [
    rootItem,
    {
      path: pathname,
      breadcrumbName: resolveLabel({ id: fallbackLabelId }),
    },
  ];
}
